/**
 * Mobile Header Component
 *
 * Top bar for mobile layout with search and user avatar.
 */

import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { cn } from '@/shared/lib';
import { useUIStore } from '@/state/stores/ui.store';
import { useAuthStore } from '@/core/auth/auth.store';
import { MODAL_IDS, DRAWER_IDS } from '../../types';
import { NotificationBell } from '@/features/user-notifications';

interface MobileHeaderProps {
  className?: string;
}

export const MobileHeader = ({ className }: MobileHeaderProps) => {
  const { t } = useTranslation('navigation');
  const { openModal, openDrawer } = useUIStore();
  const { user } = useAuthStore();

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const handleSearchClick = () => {
    openModal(MODAL_IDS.SEARCH);
  };

  const handleAvatarClick = () => {
    openDrawer(DRAWER_IDS.MOBILE_NAV);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 bg-white border-b border-neutral-200',
        'safe-area-inset-top',
        className
      )}
    >
      <div className="h-14 px-4 flex items-center justify-between">
        {/* Left: Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">B</span>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          {/* Search */}
          <button
            onClick={handleSearchClick}
            className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            title={t('header.search')}
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* User Avatar - Opens Drawer */}
          <button
            onClick={handleAvatarClick}
            className="ml-1 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium hover:opacity-90 transition-opacity"
            title={t('header.profile')}
          >
            {initials}
          </button>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
